import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ChevronLeft, ChevronRight, Loader2, Filter, RotateCw, Ban } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../../components/ui/alert-dialog';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export const ESTADOS_REINTENTO = {
  pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-700' },
  procesando: { label: 'Procesando', cls: 'bg-blue-100 text-blue-700' },
  exitoso: { label: 'Exitoso', cls: 'bg-green-100 text-green-700' },
  agotado: { label: 'Agotado', cls: 'bg-red-100 text-red-700' },
  descartado: { label: 'Descartado', cls: 'bg-gray-100 text-gray-600' },
};

export const fmtFecha = (iso) => iso ? new Date(iso).toLocaleString('es-PE', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

const AccionesReintento = ({ row, onDone }) => {
  const [busy, setBusy] = useState(null);
  const ejecutar = async (accion) => {
    setBusy(accion);
    try {
      const resp = await api.post(`/entregas/webhook/reintentos/${row.id}/${accion}`);
      const estado = resp.data?.estado;
      toast.success(accion === 'descartar' ? 'Reintento descartado'
        : `Reintento ejecutado: ${ESTADOS_REINTENTO[estado]?.label || estado}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar la acción');
    } finally {
      setBusy(null);
    }
  };
  if (row.estado !== 'pendiente') return <span className="text-[#ccc]">—</span>;
  return (
    <div className="flex gap-1 justify-end">
      <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!busy}
        onClick={() => ejecutar('reintentar-ahora')} data-testid={`reintentar-${row.proforma}`}>
        {busy === 'reintentar-ahora' ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCw size={12} className="mr-1" />}
        Reintentar ahora
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600" disabled={!!busy}
            data-testid={`descartar-${row.proforma}`}>
            {busy === 'descartar' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Ban size={12} className="mr-1" />}
            Descartar
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="confirmar-descartar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar el reintento de {row.proforma}?</AlertDialogTitle>
            <AlertDialogDescription>
              Ya no se volverá a consultar el CRM para esta proforma. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirmar-descartar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => ejecutar('descartar')} className="bg-red-600 hover:bg-red-700"
              data-testid="confirmar-descartar-ok">Descartar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TabReintentos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esAdmin = user?.rol_nombre === 'Administrador';
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [estado, setEstado] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (estado !== 'all') params.append('estado', estado);
      const resp = await api.get(`/entregas/webhook/reintentos?${params.toString()}`);
      setData(resp.data?.data || []);
      setTotal(resp.data?.total || 0);
      setTotalPages(resp.data?.total_pages || 0);
    } catch (err) {
      console.error('Error loading reintentos:', err);
    } finally {
      setLoading(false);
    }
  }, [page, estado]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Card className="border-[#E8E8E7] shadow-sm" data-testid="tab-reintentos-content">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8E8E7]">
        <Filter size={14} className="text-[#888]" />
        <Select value={estado} onValueChange={(v) => { setEstado(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filtro-reintento-estado">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(ESTADOS_REINTENTO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-[#888] ml-auto" data-testid="reintentos-total">{total} registros</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tabla-reintentos">
          <thead>
            <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
              {['Recibido', 'Proforma', 'Estado', 'Intentos', 'Próximo intento', 'Último error', 'Entrega'].map(h => (
                <th key={h} className="text-left px-4 py-2.5 font-medium text-[#666] whitespace-nowrap">{h}</th>
              ))}
              {esAdmin && <th className="text-right px-4 py-2.5 font-medium text-[#666]">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12"><Loader2 className="animate-spin mx-auto text-[#9d8272]" size={24} /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#888]">Sin proformas en cola de reintento</td></tr>
            ) : data.map(row => {
              const est = ESTADOS_REINTENTO[row.estado] || { label: row.estado, cls: '' };
              return (
                <tr key={row.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`reintento-row-${row.proforma}`}>
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap">{fmtFecha(row.created_at)}</td>
                  <td className="px-4 py-2.5 font-mono text-[#333]">{row.proforma}</td>
                  <td className="px-4 py-2.5"><Badge className={`${est.cls} hover:${est.cls} text-[10px]`}>{est.label}</Badge></td>
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap">{row.intentos}/{row.max_intentos}</td>
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap" data-testid={`reintento-proximo-${row.proforma}`}>
                    {row.estado === 'pendiente' ? fmtFecha(row.proximo_intento_at) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-[#888] max-w-[220px] truncate" title={row.ultimo_error || ''}>{row.ultimo_error || '—'}</td>
                  <td className="px-4 py-2.5">
                    {row.entrega_codigo ? (
                      <button onClick={() => navigate(`/entregas/${row.entrega_id}`)}
                        className="text-[#9d8272] font-semibold hover:underline">{row.entrega_codigo}</button>
                    ) : <span className="text-[#ccc]">—</span>}
                  </td>
                  {esAdmin && <td className="px-4 py-2.5"><AccionesReintento row={row} onDone={loadData} /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E8E7]">
        <span className="text-xs text-[#888]">Mostrando {data.length} de {total}</span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1} className="h-7 w-7 p-0" data-testid="reintentos-prev"><ChevronLeft size={14} /></Button>
          <span className="text-xs px-2">Pág. {page} de {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages} className="h-7 w-7 p-0" data-testid="reintentos-next"><ChevronRight size={14} /></Button>
        </div>
      </div>
    </Card>
  );
};

export default TabReintentos;
